import { ColumnState } from '@ag-grid-community/core';
import { LuceneExpression } from 'lucene';

import { DatacenterDefaultConfiguration } from '../../shared/datacenter/datacenter-default-configuration';
import { DatacenterType, UserDatacenterView } from '../../shared/models/datacenter-aggrid-view';
import { DatacenterFilterUtilsService } from '../../shared/services/datacenter-filter-utils.service';
import { DatacenterViewService } from '../../shared/services/datacenter-view.service';
import { ColDefWithFields } from '../../shared/datacenter/column-type';

interface IECallOffViewService {
	convertViewToColumnDefinitions(
		view: UserDatacenterView,
		allColumns: ColDefWithFields[]
	): (ColDefWithFields & ColumnState)[];
	convertViewToFilterModel(view: UserDatacenterView): Record<string, unknown> | undefined;
	convertViewToLuceneFilter(view: UserDatacenterView): LuceneExpression | undefined;
	getAllColumnDefinitions(): ReturnType<DatacenterDefaultConfiguration['getAllColumnsOfDatacenter']>;
	getCurrentView(defaultView: UserDatacenterView): UserDatacenterView;
	getFieldsForView(view: UserDatacenterView): string[];
	getDataCenterType(): string;
	getDefaultView(): UserDatacenterView;
	getViewList(defaultView: UserDatacenterView): UserDatacenterView[];
	saveCurrentView(view: UserDatacenterView): Promise<void>;
	setViewToEdit(view: UserDatacenterView): void;
}

export class ECallOffViewService implements IECallOffViewService {
	constructor(
		private readonly datacenterType: DatacenterType,
		private readonly defaultConfiguration: DatacenterDefaultConfiguration,
		private readonly filterUtils: DatacenterFilterUtilsService,
		private readonly viewService: DatacenterViewService
	) {}

	convertViewToColumnDefinitions(
		view: UserDatacenterView,
		allColumns: ColDefWithFields[]
	): (ColDefWithFields & ColumnState)[] {
		return this.viewService.convertViewToColumnDefinitions(view, allColumns);
	}

	convertViewToFilterModel(view: UserDatacenterView): Record<string, unknown> | undefined {
		return this.filterUtils.getSteelUserFilters(view);
	}

	convertViewToLuceneFilter(view: UserDatacenterView): LuceneExpression | undefined {
		return this.filterUtils.getLuceneFromAllFilters(view);
	}

	getAllColumnDefinitions(): ReturnType<DatacenterDefaultConfiguration['getAllColumnsOfDatacenter']> {
		return this.defaultConfiguration.getAllColumnsOfDatacenter(this.datacenterType);
	}

	/**
	 * Get the current view for the given default view.
	 * @param defaultView - The default datacenter view
	 * @returns - The current user view
	 */
	getCurrentView(defaultView: UserDatacenterView): UserDatacenterView {
		return this.viewService.getCurrentView(defaultView);
	}

	/**
	 * Build the API field list required by the given view: the fields backing the
	 * visible columns plus the datacenter's mandatory fields. Mirrors the generic
	 * datacenter updateCurrentColumns() so only the needed fields are requested.
	 * @param view - The user datacenter view
	 * @returns - The array of required field names
	 */
	getFieldsForView(view: UserDatacenterView): string[] {
		const knownColIds = new Set(this.getAllColumnDefinitions().map(column => column.colId));
		const colIds = new Set([
			...view.columnState.map(column => column.colId),
			...(view.filterState ?? []).map(filter => filter.id).filter(id => knownColIds.has(id))
		]);
		const fields = [
			...new Set([...colIds]
				.flatMap(colId => this.defaultConfiguration.getFieldsForColumn(view.type, colId)))
		];

		for (const field of this.defaultConfiguration.getRequiredFields(view.type)) {
			if (!fields.includes(field)) {
				fields.push(field);
			}
		}
		return fields;
	}

	getDataCenterType(): string {
		return this.datacenterType;
	}

	getDefaultView(): UserDatacenterView {
		return this.defaultConfiguration.getDefaultView(this.datacenterType);
	}

	getViewList(defaultView: UserDatacenterView): UserDatacenterView[] {
		const views: UserDatacenterView[] = [];
		views.push(defaultView);
		for (const view of this.viewService.getViewList(this.datacenterType)) {
			views.push(view);
		}
		return views;
	}

	async saveCurrentView(view: UserDatacenterView): Promise<void> {
		await this.viewService.saveCurrentView(view);
	}

	setViewToEdit(view: UserDatacenterView): void {
		this.viewService.viewToEdit = {
			currentView: view,
			viewType: this.datacenterType
		};
	}
}
